import { useState, useEffect, useRef, useCallback } from 'react';
import * as React from 'react';
import {
    CarouselWrapper,
    SlideWrapper,
    Slider,
    CardWrapper,
    ButtonGroup,
    NavButton as Button
} from './Carousel.styles';
import { ArrowLeftMedium, ArrowRightMedium } from '@blueprint/icons';

import { CarouselProps } from './Carousel.types';

const defaultBreakpoints = [
    { size: 480, numCards: 2 },
    { size: 768, numCards: 3 },
    { size: 1024, numCards: 4 }
];

export default function Carousel({
    children,
    breakpoints,
    singleScroll = false
}: CarouselProps) {
    const numChildren = React.Children.count(children);
    const responsiveConfig = breakpoints
        ? breakpoints.sort(function (a, b) {
              return a.size - b.size;
          })
        : defaultBreakpoints;

    const SliderRef = useRef<HTMLDivElement>(null);
    const [currentCard, setCurrentCard] = useState(0);
    const [numVisibleCards, setNumVisibleCards] = useState(1.15);
    const [slideWidth, setSlideWidth] = useState(100 / numVisibleCards);
    const [childWidth, setChildWidth] = useState(0);
    const [maxShift, setMaxShift] = useState(
        (numChildren - 1) * childWidth - childWidth / 4
    );
    const [disablePreviousButton, setDisablePreviousButton] = useState(true);
    const [disableNextButton, setDisableNextButton] = useState(false);
    const [hideButtons, setHideButtons] = useState(
        numChildren <= numVisibleCards
    );
    const previousButton = () => {
        const element = SliderRef.current;
        const cardsToScroll =
            currentCard - (singleScroll ? 1 : Math.floor(numVisibleCards));
        element?.scroll({
            top: 0,
            left: cardsToScroll * childWidth,
            behavior: 'smooth'
        });
    };
    const nextButton = () => {
        const element = SliderRef.current;
        const cardsToScroll =
            currentCard + (singleScroll ? 1 : Math.floor(numVisibleCards));
        element?.scroll({
            top: 0,
            left: cardsToScroll * childWidth,
            behavior: 'smooth'
        });
    };

    const handleScroll = useCallback(() => {
        const element = SliderRef.current as HTMLElement;
        const scrollLeft = element.scrollLeft;
        if (scrollLeft === 0) {
            setCurrentCard(0);
        } else if (
            scrollLeft + element.offsetWidth >= numChildren * childWidth ||
            scrollLeft >= maxShift
        ) {
            const newCurrentCard = Math.ceil(numChildren - numVisibleCards);
            if (newCurrentCard !== currentCard) {
                setCurrentCard(newCurrentCard);
            }
        } else {
            const newCurrentCard = Math.floor(scrollLeft / childWidth);
            if (newCurrentCard !== currentCard) {
                setCurrentCard(newCurrentCard);
            }
        }
    }, [childWidth, currentCard, maxShift, numChildren, numVisibleCards]);

    useEffect(() => {
        const docWidth = window.innerWidth;
        const slider = SliderRef.current as HTMLElement;
        const getNumbVisibleCards = () => {
            let numVisibleCards = 1.15;
            responsiveConfig.forEach(({ size, numCards }) => {
                if (size <= window.innerWidth) {
                    numVisibleCards = numCards;
                }
            });
            return numChildren <= numVisibleCards
                ? numChildren
                : numVisibleCards;
        };

        const updateView = () => {
            if (docWidth !== window.innerWidth) {
                slider.scrollLeft = 0;
                const newNumVisibleCards = getNumbVisibleCards();
                setNumVisibleCards(newNumVisibleCards);
                const newSlideWidth = 100 / newNumVisibleCards;
                setSlideWidth(newSlideWidth);
                const newChildWidth = Math.floor(
                    slider.offsetWidth / newNumVisibleCards
                );
                setChildWidth(newChildWidth);
                const newMaxShift =
                    (numChildren - 1) * newChildWidth - newChildWidth / 4;
                setMaxShift(newMaxShift);
                setHideButtons(numChildren <= newNumVisibleCards);
            }
        };

        const newNumVisibleCards = getNumbVisibleCards();
        setNumVisibleCards(newNumVisibleCards);
        setSlideWidth(100 / newNumVisibleCards);
        const newChildWidth = Math.floor(
            slider.offsetWidth / newNumVisibleCards
        );
        setChildWidth(newChildWidth);
        const newMaxShift =
            (numChildren - 1) * newChildWidth - newChildWidth / 4;
        setMaxShift(newMaxShift);
        setDisablePreviousButton(currentCard <= 0);
        setDisableNextButton(currentCard + newNumVisibleCards >= numChildren);
        setHideButtons(numChildren <= newNumVisibleCards);

        window.addEventListener('resize', updateView);

        return function cleanup() {
            window.removeEventListener('resize', updateView);
        };
    }, [
        currentCard,
        handleScroll,
        maxShift,
        numChildren,
        responsiveConfig,
        slideWidth
    ]);

    return (
        <CarouselWrapper peekingSlides={numVisibleCards % 1 !== 0}>
            <SlideWrapper ref={SliderRef} onScroll={handleScroll}>
                <Slider>
                    {React.Children.map(
                        children,
                        (element: React.ReactNode, index: number) => {
                            return (
                                <CardWrapper key={index} width={slideWidth}>
                                    {element}
                                </CardWrapper>
                            );
                        }
                    )}
                </Slider>
            </SlideWrapper>
            {!hideButtons && (
                <ButtonGroup>
                    <Button
                        variant="secondary"
                        aria-label="Previous slide"
                        iconOnly={true}
                        onClick={previousButton}
                        disabled={disablePreviousButton}
                    >
                        <ArrowLeftMedium />
                    </Button>
                    <Button
                        variant="secondary"
                        aria-label="Next slide"
                        iconOnly={true}
                        onClick={nextButton}
                        disabled={disableNextButton}
                    >
                        <ArrowRightMedium />
                    </Button>
                </ButtonGroup>
            )}
        </CarouselWrapper>
    );
}
